import { useState, useCallback, useRef } from 'react';
import {
  Phone,
  Mail,
  User,
  Calendar,
  DollarSign,
  Package,
  CheckCircle2,
  XCircle,
  Plus,
  Clock,
  MessageCircle,
  RefreshCw,
  X,
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../../ui/sheet';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Textarea } from '../../ui/textarea';
import { Label } from '../../ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { cn } from '../../../lib/utils';
import type { RepescagemLeadDetail, RepescagemStage } from './types';
import {
  STAGE_CONFIG,
  CHANNEL_LABELS,
  OUTCOME_LABELS,
  formatCurrency,
  formatDate,
  formatDateTime,
} from './types';
import { RepescagemAttemptDialog, type AttemptFormData } from './RepescagemAttemptDialog';

interface RepescagemDetailDrawerProps {
  lead: RepescagemLeadDetail | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAddAttempt: (leadId: string, data: AttemptFormData) => Promise<void>;
  onChangeStage: (leadId: string, stage: RepescagemStage, notes?: string) => Promise<void>;
  onUpdateNotes: (leadId: string, notes: string) => Promise<void>;
  isSubmittingAttempt: boolean;
}

const CHANNEL_ICONS: Record<string, React.ReactNode> = {
  TELEFONE: <Phone className="h-3.5 w-3.5" />,
  WHATSAPP: <MessageCircle className="h-3.5 w-3.5" />,
  EMAIL: <Mail className="h-3.5 w-3.5" />,
};

const OUTCOME_COLORS: Record<string, string> = {
  NAO_ATENDEU: 'text-orange-500',
  PEDIU_RETORNO: 'text-amber-500',
  CONTATO_REALIZADO: 'text-green-500',
  CAIXA_POSTAL: 'text-gray-500',
  NUMERO_INVALIDO: 'text-red-500',
  EMAIL_ENVIADO: 'text-blue-500',
};

export function RepescagemDetailDrawer({
  lead,
  open,
  onOpenChange,
  onAddAttempt,
  onChangeStage,
  onUpdateNotes,
  isSubmittingAttempt,
}: RepescagemDetailDrawerProps) {
  const [attemptDialogOpen, setAttemptDialogOpen] = useState(false);
  const [generalNotes, setGeneralNotes] = useState('');
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [isChangingStage, setIsChangingStage] = useState(false);
  const notesInitializedRef = useRef<string | null>(null);

  // Initialize notes when lead changes
  if (lead && notesInitializedRef.current !== lead.id) {
    notesInitializedRef.current = lead.id;
    setGeneralNotes(lead.generalNotes ?? '');
  }

  const handleNotesBlur = useCallback(async () => {
    if (!lead) return;
    const currentNotes = lead.generalNotes ?? '';
    if (generalNotes === currentNotes) return;
    setIsSavingNotes(true);
    try {
      await onUpdateNotes(lead.id, generalNotes);
    } finally {
      setIsSavingNotes(false);
    }
  }, [lead, generalNotes, onUpdateNotes]);

  const handleAttemptSubmit = useCallback(
    async (data: AttemptFormData) => {
      if (!lead) return;
      await onAddAttempt(lead.id, data);
      setAttemptDialogOpen(false);
    },
    [lead, onAddAttempt]
  );

  const handleChangeStage = useCallback(
    async (stage: RepescagemStage) => {
      if (!lead) return;
      setIsChangingStage(true);
      try {
        await onChangeStage(lead.id, stage);
      } finally {
        setIsChangingStage(false);
      }
    },
    [lead, onChangeStage]
  );

  if (!lead) return null;

  const stageConfig = STAGE_CONFIG[lead.stage];
  const isRejected = lead.quoteStatus === 'ORCAMENTO_RECUSADO';
  const canMoveToInteressado = lead.stage !== 'INTERESSADO';
  const canMoveToDeclinado = lead.stage !== 'DECLINADO';

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          className="!inset-3 !w-auto !max-w-full !h-auto !border !rounded-xl p-0 flex flex-col [&>button:last-child]:hidden"
        >
          {/* Fixed header with X button */}
          <div className="flex-none border-b px-4 py-3">
            <SheetHeader>
              <div className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <SheetTitle className="text-sm leading-tight">
                    #{lead.quoteNumber} — {lead.clientName}
                  </SheetTitle>
                  <SheetDescription className="sr-only">
                    Detalhes do lead de repescagem
                  </SheetDescription>
                </div>
                <span
                  className={cn(
                    'inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full border flex-shrink-0',
                    stageConfig.color
                  )}
                >
                  {stageConfig.label}
                </span>
                <button
                  type="button"
                  onClick={() => onOpenChange(false)}
                  className="flex-shrink-0 rounded-sm p-1 opacity-70 hover:opacity-100 hover:bg-muted transition-opacity"
                >
                  <X className="h-4 w-4" />
                  <span className="sr-only">Fechar</span>
                </button>
              </div>
            </SheetHeader>
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
            {/* Quote info + contacts in a 2-column layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Quote info */}
              <section>
                <h3 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                  Informações do Orçamento
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex items-center gap-1.5">
                    <Calendar className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                    <span className="text-xs">{formatDate(lead.quoteDate)}</span>
                  </div>
                  {lead.sellerName && (
                    <div className="flex items-center gap-1.5">
                      <User className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                      <span className="text-xs truncate">{lead.sellerName}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-1.5">
                    <RefreshCw className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                    <Badge
                      variant="outline"
                      className={cn(
                        'text-[10px] px-1.5 py-0 h-5',
                        isRejected
                          ? 'bg-red-500/10 text-red-500 border-red-500/20'
                          : 'bg-blue-500/10 text-blue-500 border-blue-500/20'
                      )}
                    >
                      {lead.quoteStatusLabel}
                    </Badge>
                  </div>
                  {lead.totalValue !== null && (
                    <div className="flex items-center gap-1.5">
                      <DollarSign className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                      <span className="text-xs font-semibold text-green-500">
                        {formatCurrency(lead.totalValue)}
                      </span>
                    </div>
                  )}
                </div>
              </section>

              {/* Contacts */}
              <section>
                <h3 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                  Contatos
                </h3>
                <div className="space-y-1">
                  {lead.contactNames.length > 0 && (
                    <div className="flex items-center gap-1.5">
                      <User className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                      <span className="text-xs">{lead.contactNames.join(', ')}</span>
                    </div>
                  )}
                  {lead.contactPhones.map((phone) => (
                    <a
                      key={phone}
                      href={`tel:${phone}`}
                      className="flex items-center gap-1.5 text-xs text-primary hover:underline"
                    >
                      <Phone className="h-3.5 w-3.5 flex-shrink-0" />
                      {phone}
                    </a>
                  ))}
                  {lead.contactEmails.map((email) => (
                    <a
                      key={email}
                      href={`mailto:${email}`}
                      className="flex items-center gap-1.5 text-xs text-primary hover:underline truncate"
                    >
                      <Mail className="h-3.5 w-3.5 flex-shrink-0" />
                      {email}
                    </a>
                  ))}
                  {lead.clientEmail && !lead.contactEmails.includes(lead.clientEmail) && (
                    <a
                      href={`mailto:${lead.clientEmail}`}
                      className="flex items-center gap-1.5 text-xs text-muted-foreground hover:underline truncate"
                    >
                      <Mail className="h-3.5 w-3.5 flex-shrink-0" />
                      {lead.clientEmail}
                      <span className="text-[10px]">(empresa)</span>
                    </a>
                  )}
                  {lead.contactNames.length === 0 &&
                    lead.contactPhones.length === 0 &&
                    lead.contactEmails.length === 0 && (
                      <p className="text-xs text-muted-foreground">Nenhum contato</p>
                    )}
                </div>
              </section>
            </div>

            {/* Quote items */}
            {lead.items.length > 0 && (
              <section>
                <h3 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">
                  <Package className="h-3 w-3 inline mr-1" />
                  Itens do Orçamento
                </h3>
                <div className="rounded-md border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-[10px] py-1.5">Produto</TableHead>
                        <TableHead className="text-[10px] text-right py-1.5">Qtd</TableHead>
                        <TableHead className="text-[10px] text-right py-1.5">Unit.</TableHead>
                        <TableHead className="text-[10px] text-right py-1.5">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {lead.items.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="text-xs py-1.5">
                            <p className="font-medium">{item.name}</p>
                            {item.description && (
                              <p className="text-muted-foreground text-[10px] line-clamp-1">
                                {item.description}
                              </p>
                            )}
                          </TableCell>
                          <TableCell className="text-xs text-right py-1.5">{item.quantity}</TableCell>
                          <TableCell className="text-xs text-right py-1.5">
                            {formatCurrency(item.unitPrice)}
                          </TableCell>
                          <TableCell className="text-xs text-right font-medium py-1.5">
                            {formatCurrency(item.quantity * item.unitPrice)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </section>
            )}

            {/* Payment observations - collapsible */}
            {lead.paymentObservations && (
              <section>
                <h3 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">
                  Observações de Pagamento
                </h3>
                <p className="text-xs text-muted-foreground whitespace-pre-wrap rounded-md bg-muted/30 p-2 border max-h-24 overflow-y-auto">
                  {lead.paymentObservations}
                </p>
              </section>
            )}

            {/* Attempt history */}
            <section>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
                  <Clock className="h-3 w-3 inline mr-1" />
                  Tentativas ({lead.attemptCount}/{lead.maxAttempts})
                </h3>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-6 text-[10px] px-2"
                  onClick={() => setAttemptDialogOpen(true)}
                  disabled={isSubmittingAttempt}
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Nova Tentativa
                </Button>
              </div>

              {lead.attempts.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-2">
                  Nenhuma tentativa registrada
                </p>
              ) : (
                <div className="space-y-1.5">
                  {[...lead.attempts]
                    .sort((a, b) => b.attemptNumber - a.attemptNumber)
                    .map((attempt) => (
                      <div
                        key={attempt.id}
                        className="rounded-md border bg-muted/20 px-2.5 py-1.5 space-y-0.5"
                      >
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[10px] font-bold text-muted-foreground">
                            #{attempt.attemptNumber}
                          </span>
                          <span className="text-[10px] text-muted-foreground">
                            {formatDateTime(attempt.attemptedAt)}
                          </span>
                          <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
                            {CHANNEL_ICONS[attempt.channel]}
                            {CHANNEL_LABELS[attempt.channel] ?? attempt.channel}
                          </span>
                          <span
                            className={cn(
                              'text-[10px] font-medium',
                              OUTCOME_COLORS[attempt.outcome] ?? 'text-foreground'
                            )}
                          >
                            {OUTCOME_LABELS[attempt.outcome] ?? attempt.outcome}
                          </span>
                          {attempt.sdrName && (
                            <span className="text-[10px] text-muted-foreground ml-auto">
                              {attempt.sdrName}
                            </span>
                          )}
                        </div>
                        {attempt.notes && (
                          <p className="text-[10px] text-muted-foreground italic pl-4">
                            "{attempt.notes}"
                          </p>
                        )}
                      </div>
                    ))}
                </div>
              )}
            </section>

            {/* General notes */}
            <section>
              <div className="flex items-center gap-2 mb-1">
                <Label
                  htmlFor="general-notes"
                  className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide"
                >
                  Notas Gerais
                </Label>
                {isSavingNotes && (
                  <span className="text-[10px] text-muted-foreground">Salvando...</span>
                )}
              </div>
              <Textarea
                id="general-notes"
                value={generalNotes}
                onChange={(e) => setGeneralNotes(e.target.value)}
                onBlur={handleNotesBlur}
                placeholder="Adicione observações sobre este lead..."
                rows={2}
                className="resize-none text-xs"
              />
            </section>
          </div>

          {/* Fixed footer with stage actions */}
          <div className="flex-none border-t px-4 py-3 bg-background">
            <div className="flex gap-2">
              {canMoveToInteressado && (
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-1 h-8 text-xs border-green-500/40 text-green-500 hover:bg-green-500/10 hover:text-green-500"
                  onClick={() => handleChangeStage('INTERESSADO')}
                  disabled={isChangingStage}
                >
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                  Interessado
                </Button>
              )}
              {canMoveToDeclinado && (
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-1 h-8 text-xs border-red-500/40 text-red-500 hover:bg-red-500/10 hover:text-red-500"
                  onClick={() => handleChangeStage('DECLINADO')}
                  disabled={isChangingStage}
                >
                  <XCircle className="h-3.5 w-3.5 mr-1.5" />
                  Declinado
                </Button>
              )}
            </div>
            {/* Re-engagement info */}
            {(lead.reengagementCount > 0 || lead.cooldownUntil) && (
              <div className="mt-1.5">
                {lead.reengagementCount > 0 && (
                  <p className="text-[10px] text-muted-foreground">
                    Ciclo #{lead.reengagementCount}
                    {lead.reengagementAt && ` — Reativado em ${formatDate(lead.reengagementAt)}`}
                  </p>
                )}
                {lead.cooldownUntil && (
                  <p className="text-[10px] text-amber-500">
                    Cooldown até {formatDate(lead.cooldownUntil)}
                  </p>
                )}
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <RepescagemAttemptDialog
        open={attemptDialogOpen}
        onOpenChange={setAttemptDialogOpen}
        onSubmit={handleAttemptSubmit}
        isSubmitting={isSubmittingAttempt}
        leadName={lead.clientName}
        attemptNumber={lead.attemptCount + 1}
      />
    </>
  );
}
